import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'
import { ClipboardList } from 'lucide-react'
import { usePublicBooking } from '@/stores/booking.store'
import { setToken } from '@/stores/token.store'
import { countUnansweredRequiredQuestions } from '@/lib/booking-questionnaire'
import {
  questionnaireGateContinuation,
  questionnaireGatesPayment,
  validateQuestionnaireGateSearch,
} from '@/lib/questionnaire-gate'
import { BookingQuestionnaireSection } from '@/components/booking-questionnaire-section'
import { PortalHeader } from '@/components/portal-header'
import { PortalFooter } from '@/components/portal-footer'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Questionnaire step that sits between quote acceptance (or a Pay click) and checkout when the
 * organization requires the questionnaire before payment. When nothing is gating payment it
 * passes straight through, so callers can route here unconditionally.
 */
export const Route = createFileRoute('/booking/$id/questionnaire')({
  component: BookingQuestionnaireGatePage,
  validateSearch: validateQuestionnaireGateSearch,
})

function BookingQuestionnaireGatePage() {
  const { id } = useParams({ from: '/booking/$id/questionnaire' })
  const search = useSearch({ from: '/booking/$id/questionnaire' })
  const navigate = useNavigate()
  const { booking, isLoading, hasLoaded, isError } = usePublicBooking(id)
  const hasContinuedRef = useRef(false)

  useEffect(() => {
    if (!search.token) return
    setToken(id, search.token)
    navigate({
      to: '/booking/$id/questionnaire',
      params: { id },
      search: { invoice_id: search.invoice_id, schedule_id: search.schedule_id },
      replace: true,
    })
  }, [id, navigate, search.invoice_id, search.schedule_id, search.token])

  const continueToPayment = useCallback(() => {
    if (hasContinuedRef.current) return
    hasContinuedRef.current = true
    const next = questionnaireGateContinuation(search)
    if (next.to === 'checkout') {
      navigate({
        to: '/booking/$id/checkout',
        params: { id },
        search: { invoice_id: next.invoiceId, schedule_id: next.scheduleId },
        replace: true,
      })
      return
    }
    navigate({ to: '/booking/$id', params: { id }, search: {}, replace: true })
  }, [id, navigate, search])

  const isGated = questionnaireGatesPayment(booking)
  const isReady = !isLoading && hasLoaded && !search.token

  useEffect(() => {
    if (!isReady) return
    // A load failure falls through to the booking page, which owns the no-access state.
    if (isError || !booking || !isGated) continueToPayment()
  }, [booking, continueToPayment, isError, isGated, isReady])

  const handleBack = useCallback(() => {
    navigate({ to: '/booking/$id', params: { id }, search: {} })
  }, [id, navigate])

  if (!isReady || !booking || !isGated || !booking.questionnaire) {
    return <QuestionnaireGateSkeleton />
  }

  const unanswered = countUnansweredRequiredQuestions(booking.questionnaire)
  const isResumingPayment = questionnaireGateContinuation(search).to === 'checkout'

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <PortalHeader
        orgName={booking.organization?.name || 'Booking'}
        logoUrl={booking.organization?.logoUrl}
        contactEmail={booking.organization?.contactEmail}
        hasGallery={!!booking.organization?.galleryImages?.length}
        backLabel="Return to booking"
        onBack={handleBack}
        maxWidth="5xl"
      />

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-4 px-4 py-6 sm:gap-6 sm:py-8 md:px-8">
        <div className="flex items-start gap-3 rounded-lg border bg-card p-4">
          <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <h1 className="text-base font-semibold">One step before payment</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {booking.organization?.name || 'This business'} needs a few details about your event before you pay.
              {' '}
              {unanswered === 1 ? '1 required question is' : `${unanswered} required questions are`} still unanswered.
            </p>
          </div>
        </div>

        <BookingQuestionnaireSection
          bookingId={id}
          questionnaire={booking.questionnaire}
          title="Event details"
          description="Share a few details so we can prepare for your event."
          submitLabel={isResumingPayment ? 'Save and continue to payment' : 'Save answers'}
          onCompleted={continueToPayment}
        />
      </main>

      {booking.organization && <PortalFooter organization={booking.organization} />}
    </div>
  )
}

function QuestionnaireGateSkeleton() {
  return (
    <div className="min-h-svh">
      <header className="z-10 border-b border-border bg-card md:sticky md:top-0">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:py-4 md:px-8">
          <Skeleton className="h-10 w-52" />
          <Skeleton className="h-9 w-36" />
        </div>
      </header>
      <main className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-6 sm:gap-6 sm:py-8 md:px-8">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-96 w-full" />
      </main>
    </div>
  )
}
